import { get } from "lodash";
import { useMemo, useState } from "react";
import { Modal } from "components/modal";
import Pagination from "components/pagination";
import { generateUUID } from "utills/helpers";
import { downloadTxtFile } from "routes/group/utils/helper";
import { DownloadIcon } from "@heroicons/react/outline";
import Button from "components/button";

export interface ImportAttendantErrorType {
  row: number;
  product_id: string;
  column: string;
  column_key: string;
  invalid_value: string;
  message: string;
  valid_options: string[];
}

interface PagingType {
  total_item: number;
  total_page: number;
  current_page: number;
}

interface PropType {
  data: ImportAttendantErrorType[];
  open: boolean;
  handleClose: () => void;
}

const ImportAttendantErrorModal = (props: PropType) => {
  const { data, open, handleClose } = props;

  const PAGE_SIZE = 5;

  const [page, setPage] = useState<number>(1);

  // Paginated invalid import data
  const PAGINATED_DATA = useMemo(() => {
    if (!data) return [];

    const start = (page - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;

    return data?.slice(start, end);
  }, [data, page]);

  // Invalid import paging data
  const invalidPaging: PagingType = useMemo(() => {
    return {
      total_item: data?.length || 0,
      total_page: Math.ceil((data?.length || 0) / PAGE_SIZE),
      current_page: page,
    };
  }, [data, page]);

  const handlePageChange = (value: number) => {
    setPage(value);
  };

  const handleCloseModel = () => {
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleCloseModel}
      title={`Import Attendants Error`}
      closeOnClickBackdrop={false}
    >
      <div className="p-5 flex flex-col gap-5">
        <div className="w-full max-h-[500px] overflow-y-auto">
          <table className="mb-6 w-full divide-y divide-gray-300 bg-magnolia-500 rounded-md">
            <thead>
              <tr>
                <th scope="col" className="th">
                  Row
                </th>

                <th scope="col" className="th">
                  Product ID
                </th>

                <th scope="col" className="th">
                  Column
                </th>

                <th scope="col" className="th">
                  Column key
                </th>

                <th scope="col" className="th">
                  Invalid value
                </th>

                <th scope="col" className="th">
                  Message
                </th>

                <th scope="col" className="th">
                  Valid options
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200 bg-white">
              {PAGINATED_DATA?.map((item: ImportAttendantErrorType) => {
                const valid_options = get(item, "valid_options", []);

                return (
                  <tr key={generateUUID()}>
                    <td className="td min-w-[100px]">
                      <span>{get(item, "row", 0) + 1}</span>
                    </td>

                    <td className="td min-w-[200px]">
                      <span>{get(item, "product_id", "")}</span>
                    </td>

                    <td className="td min-w-[150px]">
                      <span>{get(item, "column", "")}</span>
                    </td>

                    <td className="td min-w-[150px]">
                      <span>{get(item, "column_key", "")}</span>
                    </td>

                    <td className="td min-w-[150px]">
                      <span>{get(item, "invalid_value", "")}</span>
                    </td>

                    <td className="td min-w-[200px]">
                      <span>{get(item, "message", "")}</span>
                    </td>

                    <td className="td min-w-[180px]">
                      {valid_options?.length !== 0 && (
                        <Button
                          type="button"
                          className="w-11 h-11 bg-white rounded-md px-3 text-brand-500 py-2
                            border-solid border-brand-500 border-[1px]
                            hover:bg-white active:bg-white active:opacity-85 sm:w-auto"
                          onClick={() =>
                            downloadTxtFile(
                              get(item, "column_key", "valid_options"),
                              valid_options,
                            )
                          }
                        >
                          <DownloadIcon className="size-4 text-brand-500" />{" "}
                          <span className="hidden sm:block sm:ml-2">
                            Download file
                          </span>
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={get(invalidPaging, "current_page", 1)}
          totalPage={get(invalidPaging, "total_page", 0)}
          total={{
            totalItems: get(PAGINATED_DATA, "length", 0),
            total: get(invalidPaging, "total_item", 0),
          }}
          onChange={handlePageChange}
        />
      </div>
    </Modal>
  );
};

export default ImportAttendantErrorModal;
